import { useCallback, useMemo, useState } from 'react'

import { getFractionExpandQuestion as gFracQues } from './FractionExpand.utils'

interface UseFractionExpandReturnType {
  question: { numerator: number; denominator: number; factor: number }
  formValid: boolean
  answer: { numerator?: string; denominator?: string }
  error?: { numerator: boolean; denominator: boolean }
  handleCheckAnswer: () => void
  onInputChange: (isNumerator?: boolean, val?: string) => void
  goToNextQuestion: () => void
}

interface UseProperFractionProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
}

function useFractionExpand({
  difficultyLevel,
  onAnswerStateChange,
  onNextQuestionClick,
}: UseProperFractionProps): UseFractionExpandReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [answer, setAnswer] = useState<{ numerator?: string; denominator?: string }>({})
  const [error, setError] = useState<{ numerator: boolean; denominator: boolean }>()
  const question = useMemo(() => gFracQues(difficultyLevel, taskHistory), [difficultyLevel, taskHistory])

  const handleCheckAnswer = useCallback((): void => {
    const { numerator = '0', denominator = '0' } = answer
    const err = {
      numerator: Number(numerator) / question.factor !== question.numerator,
      denominator: Number(denominator) / question.factor !== question.denominator,
    }
    onAnswerStateChange(!err.numerator && !err.denominator)
    setError(err)
  }, [question, answer, onAnswerStateChange, setError])

  const onInputChange = useCallback(
    (isNumerator?: boolean, val?: string) => {
      if (isNumerator === true) {
        setAnswer({ ...answer, numerator: val })
      } else if (isNumerator === false) {
        setAnswer({ ...answer, denominator: val })
      } else {
        setAnswer({ numerator: '', denominator: '' })
      }
    },
    [answer],
  )

  const goToNextQuestion = useCallback(() => {
    const { numerator, denominator, factor } = question
    const newTaskHistory = [`${factor}-${numerator}-${denominator}`, ...taskHistory].slice(0, question.maxVariation)
    setTaskHistory(newTaskHistory)
    onInputChange()
    onNextQuestionClick()
    setError(undefined)
  }, [taskHistory, onInputChange, onNextQuestionClick, question])

  return {
    question,
    formValid: !!answer.numerator && !!answer.denominator,
    answer,
    error,
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
  } as const
}

export default useFractionExpand
